/**
 * Created by Admin on 20.02.2018.
 */
import React from 'react'
import { withStyles } from '@material-ui/core'
import { connect } from 'react-redux'
import { Redirect } from 'react-router-dom'
import { clearAtom } from '../flowBuilder/actions/atom'
import { setActiveBot } from './actions/activeBot'
import { getBots, listBots, isBotListServerSortKey } from './api/bots'
import BotPlate from './components/BotPlate'
import CreateNewBot from './components/CreateNewBot'
import Bots from './components/Bots'
import BotsLegacy from './components/BotsLegacy'
import BotsToolbar from './components/BotsToolbar'
import OrganisationsTable from './components/OrganisationsTable'
import OrganisationsOverview from './components/OrganisationsOverview'
import OrganisationScopeSwitch from './components/OrganisationScopeSwitch'
import Pagination from '../../uiKit/table/Pagination'
import {
  DEFAULT_ROWS_PER_PAGE,
  DEFAULT_GRID_ROWS_PER_PAGE,
  GRID_ROWS_PER_PAGE_OPTIONS,
  isAllRowsSelection,
  normalizePageSize,
  parseRowsPerPageSelection,
} from '../../constants/pagination'
import { toOrganisationRow } from './helpers/organisationFields'
import { writePlatformCounts, getInitialPlatformCountState } from './helpers/orgPlatformCounts'
import {
  PLATFORM_FLAG,
  countQcsDevOverrideOnly,
  getQcsDevOverrideIds,
  isQcsAccountUser,
  isQcsBotPayload,
  qcsAccountLandingPath,
  shouldShowQcsScopeSegment,
  shouldSkipQcsOrganisationsHome,
} from '../../constants/qcs'
import { resolveQcsLandingTab } from './api/usageLimits'
import { isQcsHandoffSession } from '../authorization/helpers/qcsHandoffSession'
import { HOME_PATH } from '../../configs'
import {
  ROLE_OPTIONS,
  access,
  getUserRole,
  hasPermissionForFeatureInAnyBot,
} from 'security'
import LoaderScreen from '../../uiKit/loaders/loaderScreen'

const styles = () => ({})

const SORT_KEYS = {
  NAME: 'name',
  ACCESS: 'access',
}

const DEFAULT_FILTERS = {
  search: '',
  appliedSearch: '',
  roleFilter: 'all',
}

const useLegacyGrid = () => process.env.REACT_APP_ORG_TABLE_UI === '0'

class Home extends React.Component {
  state = {
    ...DEFAULT_FILTERS,
    // No sort until a column header is clicked — first /bot/list omits sortBy.
    sortBy: null,
    sortDir: null,
    page: 0,
    rowsPerPage: DEFAULT_GRID_ROWS_PER_PAGE,
    listItems: [],
    listTotal: 0,
    listReady: false,
    listLoading: false,
    // Re-read on each remount so Care ↔ QCS does not flash 0 after a prior fetch.
    ...getInitialPlatformCountState(),
  }

  componentDidMount() {
    // QCS handoff sessions are redirected to /qcs in render — skip CareBrain fetches.
    if (isQcsHandoffSession()) return
    // Full list still feeds Redux (create name checks, Menu, isQcsBot(id)).
    getBots()
    this.loadList()
    this.loadPlatformCounts()
  }

  componentDidUpdate(_prevProps, prevState) {
    const pageChanged =
      prevState.page !== this.state.page ||
      prevState.rowsPerPage !== this.state.rowsPerPage ||
      prevState.appliedSearch !== this.state.appliedSearch ||
      prevState.roleFilter !== this.state.roleFilter
    const serverSortChanged =
      (prevState.sortBy !== this.state.sortBy || prevState.sortDir !== this.state.sortDir) &&
      isBotListServerSortKey(this.state.sortBy)

    if (pageChanged || serverSortChanged) {
      this.loadList({ showLoader: true })
    }
  }

  componentWillUnmount() {
    clearTimeout(this.searchTimer)
    this.listController?.abort()
    this.countController?.abort()
    this.cloneCountController?.abort()
  }

  loadList = ({ showLoader = false } = {}) => {
    const { page, rowsPerPage, appliedSearch, sortBy, sortDir, roleFilter } = this.state
    this.listController?.abort()
    const controller = new AbortController()
    this.listController = controller
    if (showLoader) this.setState({ listLoading: true })

    listBots({
      page,
      size: normalizePageSize(rowsPerPage),
      platformFlag: PLATFORM_FLAG.CAREBRAIN,
      search: appliedSearch,
      sortBy,
      sortDir,
      role: roleFilter,
      signal: controller.signal,
    })
      .then(result => {
        if (controller.signal.aborted) return
        const total = result.total || 0
        // Unfiltered list total is the CareBrain scope badge — avoid a second
        // size=1 /bot/list for the same platform. Keep careCount when filters narrow the table.
        const listUnfiltered = roleFilter === 'all' && !String(appliedSearch || '').trim()
        this.setState(prev => {
          const careCountFetched = listUnfiltered ? true : prev.careCountFetched
          const careCount = listUnfiltered ? total : prev.careCount
          if (listUnfiltered) writePlatformCounts({ care: total })
          return {
            listItems: result.items || [],
            listTotal: total,
            listReady: true,
            listLoading: false,
            ...(listUnfiltered ? { careCount } : null),
            careCountFetched,
            countsReady: careCountFetched && prev.qcsCountFetched,
          }
        })
      })
      .catch(error => {
        if (error?.name === 'AbortError') return
        // Avoid an infinite empty/loader flash if the list call fails.
        this.setState({ listReady: true, listLoading: false })
      })
  }

  /** QCS badge only — CareBrain badge comes from unfiltered loadList total. */
  loadPlatformCounts = () => {
    this.countController?.abort()
    const controller = new AbortController()
    this.countController = controller

    listBots({
      page: 0,
      size: DEFAULT_ROWS_PER_PAGE,
      platformFlag: PLATFORM_FLAG.QCS,
      signal: controller.signal,
    })
      .then(qcs => {
        if (controller.signal.aborted) return
        const qcsCount = qcs.total || 0
        writePlatformCounts({ qcs: qcsCount })
        this.setState(prev => ({
          qcsCount,
          qcsCountFetched: true,
          countsReady: prev.careCountFetched,
        }))
      })
      .catch(error => {
        if (error?.name === 'AbortError') return
        this.setState(prev => ({
          qcsCountFetched: true,
          countsReady: prev.careCountFetched,
        }))
      })
  }

  refreshAfterCreate = () => {
    getBots()
    this.loadPlatformCounts()
    // Avoid double fetch: page change is handled by componentDidUpdate.
    if (this.state.page !== 0) {
      this.setState({ page: 0 })
    } else {
      this.loadList()
    }
  }

  /**
   * cloneBot already refreshed Redux bots. The unfiltered total keeps the CareBrain
   * badge right under filters; unsorted /bot/list is id-ascending, so the clone is last.
   */
  refreshAfterClone = () => {
    this.cloneCountController?.abort()
    const controller = new AbortController()
    this.cloneCountController = controller

    listBots({ page: 0, size: 1, platformFlag: PLATFORM_FLAG.CAREBRAIN, signal: controller.signal })
      .then(result => {
        if (controller.signal.aborted) return
        const total = result.total || 0
        // listBots resolves failures as total 0; after a clone the real total is at least 1.
        if (total === 0) {
          this.loadList()
          return
        }
        writePlatformCounts({ care: total })
        this.setState(prev => ({
          careCount: total,
          careCountFetched: true,
          countsReady: prev.qcsCountFetched,
        }))

        const { page, rowsPerPage, roleFilter, appliedSearch, sortBy } = this.state
        const defaultOrder =
          roleFilter === 'all' &&
          !String(appliedSearch || '').trim() &&
          !isBotListServerSortKey(sortBy) &&
          !isAllRowsSelection(rowsPerPage)
        if (defaultOrder) {
          const lastPage = Math.max(0, Math.ceil(total / normalizePageSize(rowsPerPage)) - 1)
          if (lastPage !== page) {
            // componentDidUpdate loads the new page.
            this.setState({ page: lastPage })
            return
          }
        }
        this.loadList()
      })
      .catch(error => {
        if (error?.name === 'AbortError') return
        this.loadList()
      })
  }

  switchBot = bot => {
    setActiveBot(bot)
    clearAtom(0)
  }

  handleSearchChange = value => {
    this.setState({ search: value })
    clearTimeout(this.searchTimer)
    this.searchTimer = setTimeout(() => {
      this.setState({ appliedSearch: value, page: 0 })
    }, 250)
  }

  handleSort = key => {
    this.setState(prev => {
      const sortDir = prev.sortBy === key && prev.sortDir === 'asc' ? 'desc' : 'asc'
      // Server-sortable columns: reset to first page so loadList refetches globally.
      if (isBotListServerSortKey(key)) {
        return { sortBy: key, sortDir, page: 0 }
      }
      return { sortBy: key, sortDir }
    })
  }

  handleRoleChange = value => {
    this.setState({ roleFilter: value, page: 0 })
  }

  handleClearFilters = () => {
    clearTimeout(this.searchTimer)
    this.setState({ ...DEFAULT_FILTERS, page: 0 })
  }

  getRows() {
    // DEV "Mark as QCS" overrides are local only — hide those bots from the
    // CareBrain page even though platformFlag=carebrain still returns them.
    const overrideIds = getQcsDevOverrideIds()
    return (this.state.listItems || [])
      .filter(bot => !overrideIds.includes(Number(bot.id)))
      .map(bot => toOrganisationRow(bot, getUserRole(bot.id, access?.botAccesses)))
  }

  /**
   * Access filter is requested via GET /bot/list?role= so pagination total
   * matches. Client role filter remains a safety net if the API ignores the param.
   * Organisation / Your access order comes from sortBy + sortDir.
   */
  getVisibleRows(rows) {
    const { roleFilter, sortBy, sortDir } = this.state

    const filtered = rows.filter(row => {
      if (roleFilter !== 'all' && row.role !== roleFilter) return false
      return true
    })

    // Unsorted keeps API order; server already ordered name / role across all pages.
    if (!sortBy || isBotListServerSortKey(sortBy)) {
      return filtered
    }

    const direction = sortDir === 'asc' ? 1 : -1

    return filtered.sort((a, b) => {
      switch (sortBy) {
        case SORT_KEYS.ACCESS:
          return (a.role || '').localeCompare(b.role || '') * direction
        default:
          return a.name.localeCompare(b.name) * direction
      }
    })
  }

  renderLegacyGrid() {
    const { bots } = this.props
    const canCreateBot = hasPermissionForFeatureInAnyBot('createBot')

    return (
      <BotsLegacy>
        {canCreateBot && <CreateNewBot onCreated={this.refreshAfterCreate} />}
        {bots.map(bot => (
          <BotPlate bot={bot} key={bot.id} switchBot={this.switchBot} />
        ))}
      </BotsLegacy>
    )
  }

  renderOrganisationsTable() {
    const {
      search,
      appliedSearch,
      roleFilter,
      sortBy,
      sortDir,
      page,
      rowsPerPage,
      listTotal,
      careCount,
      qcsCount,
      listReady,
      listLoading,
      countsReady,
    } = this.state

    const canCreateBot = hasPermissionForFeatureInAnyBot('createBot')
    const overrideOnlyCount = countQcsDevOverrideOnly(this.props.bots)
    const careCountDisplay = Math.max(0, careCount - overrideOnlyCount)
    const qcsCountDisplay = qcsCount + overrideOnlyCount
    const hasActiveFilters = roleFilter !== 'all' || Boolean(appliedSearch.trim())

    // Only bounce to QCS when this account has zero CareBrain orgs (unfiltered
    // platform count) — not when a role/search filter returns an empty page.
    // Wait for both platform totals so a seeded/partial count cannot redirect early.
    if (
      listReady &&
      countsReady &&
      careCountDisplay === 0 &&
      qcsCountDisplay > 0 &&
      !hasActiveFilters
    ) {
      return <Redirect to={{ pathname: `${HOME_PATH}/qcs`, state: { fromCareHome: true } }} />
    }

    const rows = this.getRows()
    const visibleRows = this.getVisibleRows(rows)

    const toolbar = (
      <BotsToolbar
        search={search}
        roleFilter={roleFilter}
        roleOptions={ROLE_OPTIONS}
        total={listReady ? careCountDisplay : undefined}
        shown={listReady && !listLoading && hasActiveFilters ? listTotal : undefined}
        onSearchChange={this.handleSearchChange}
        onRoleChange={this.handleRoleChange}
        onClear={hasActiveFilters ? this.handleClearFilters : undefined}
      />
    )

    // Keep the switch mounted while counts are unknown so badges do not vanish.
    // Non-Auth0 accounts with no QCS orgs hide the QCS tab (and thus the switch);
    // createBot must not force an empty QCS segment.
    const showQcsSegment =
      !countsReady || shouldShowQcsScopeSegment(this.props.adminUser, qcsCountDisplay)
    const showScopeSwitch = showQcsSegment

    return (
      <Bots
        title="CareBrain organisations"
        toolbar={toolbar}
        summary={
          <OrganisationsOverview
            total={careCountDisplay}
            loading={!listReady && !countsReady}
          />
        }
        crossLink={
          showScopeSwitch ? (
            <OrganisationScopeSwitch
              active="care"
              careHref={HOME_PATH}
              qcsHref={`${HOME_PATH}/qcs`}
              careCount={countsReady ? careCountDisplay : undefined}
              qcsCount={countsReady ? qcsCountDisplay : undefined}
              showQcs={showQcsSegment}
            />
          ) : null
        }
        action={
          canCreateBot ? <CreateNewBot onCreated={this.refreshAfterCreate} /> : null
        }>
        {!listReady || listLoading ? (
          <LoaderScreen />
        ) : (
          <>
            <OrganisationsTable
              rows={visibleRows}
              sortBy={sortBy}
              sortDir={sortDir}
              searchTerm={appliedSearch.trim()}
              hasFilters={hasActiveFilters}
              onSort={this.handleSort}
              onOpen={this.switchBot}
              onCloned={this.refreshAfterClone}
            />
            {listTotal > 0 && (
              <Pagination
                count={listTotal}
                rowsPerPage={rowsPerPage}
                rowsPerPageOptions={GRID_ROWS_PER_PAGE_OPTIONS}
                page={page}
                hasSelect
                onChangePage={(event, nextPage) => this.setState({ page: nextPage })}
                onChangeRowsPerPage={event =>
                  this.setState({
                    rowsPerPage: parseRowsPerPageSelection(event.target.value),
                    page: 0,
                  })
                }
              />
            )}
          </>
        )}
      </Bots>
    )
  }

  render() {
    // QCS handoff sessions only ever see the QCS organisations home.
    if (isQcsHandoffSession()) {
      return <Redirect replace to={`${HOME_PATH}/qcs`} />
    }
    // Wait for getAdminUser so Auth0 accounts are not painted as CareBrain first.
    if (!this.props.adminUser) {
      return <Bots title="Organisations" />
    }
    if (isQcsAccountUser(this.props.adminUser)) {
      // One-org Careco Admin / Care Manager: open that bot. Multi-org Auth0
      // accounts with CareBrain orgs stay on this page; QCS-only Auth0 users
      // are bounced by renderOrganisationsTable when careCount is 0.
      if (shouldSkipQcsOrganisationsHome(this.props.adminUser)) {
        // Wait for getBots so package / *FeatureUsed gates can pick the tab.
        // Without the bot payload, landing would always be role-only.
        if (!Array.isArray(this.props.bots) || this.props.bots.length === 0) {
          return <Bots title="Organisations" />
        }
        const access = this.props.adminUser.botAccesses[0]
        const botId = access?.botId
        const bot =
          botId != null
            ? this.props.bots.find(b => Number(b.id) === Number(botId)) ||
              (this.state.listItems || []).find(b => Number(b.id) === Number(botId))
            : null
        // One-org skip is QCS-only; a lone CareBrain organisation keeps this list.
        const skipToBot = isQcsBotPayload(bot)
          ? qcsAccountLandingPath(this.props.adminUser, resolveQcsLandingTab(access.role, bot))
          : null
        if (skipToBot) {
          return <Redirect replace to={skipToBot} />
        }
      }
    }
    if (useLegacyGrid()) {
      return this.renderLegacyGrid()
    }
    return this.renderOrganisationsTable()
  }
}

const mapStateToProps = state => ({
  bots: state.bots,
  adminUser: state.adminUser,
})

export default withStyles(styles, { withTheme: true })(connect(mapStateToProps)(Home))
